/* eslint-disable no-unused-vars */
import { action, observable, computed, when } from 'mobx';
import axios from 'axios';
import includes from 'lodash/includes';
import isPlainObject from 'lodash/isPlainObject';
import Cookies from 'js-cookie';

import { ghPromoCode } from '~/common/globals.js';
import {
    PROMO_CODE_COOKIE_NAME, PROMO_COUPON_25, PROMO_COUPON_75,
    PROMO_MAX_AMOUNT_ALLOWED, PROMO_MAX_AMOUNT_ALLOWED_MONTHLY,
    PROMO_CODE_MODAL_CLOSED_COOKIE_NAME, PROMO_CODE_ANNUAL_ONLY_STRING,
    PROMO_COUPON_ANNUAL_INCENTIVE, PROMO_COUPON_MONTHLY_15, PROMO_COUPON_TRIAL_EXPIRED
} from '~/common/utils/consts.js';
import { EndOfDayDate } from '~/common/utils/date.js';
import { isBoolean, isObject, isString } from '~/common/utils/typeCheckers';
import { offerSuiteStore } from '~/common/stores/domain/offerSuite.js';
import { cmsStore } from '~/common/stores/domain/cms';
import { setSessionStorage, getSessionStorage } from '~/common/utils/storage';
import { gtm, viewPromotionEvent } from '~/common/utils/tracking';

const domain = window.location.hostname === 'localhost' ? '' : '.grasshopper.com';

class PromoCodeClass {
    @observable promo = null;
    @observable promoSup = null;
    @observable promoTemp = null;
    @observable PromoModalViewed = false;
    @observable isPromoStored = false;
    @observable isBannerOpen = false;
    @observable userCoupon = '';
    @observable isAnnualUpgrade = false;
    @observable annualUpgradeCoupon = '';
    @observable promoMaxAmount = 0;
    @observable discounts = {};
    @observable isTrialExpired = false;

    bootstrapStore() {
        when(
            () => cmsStore.hasLoaded,
            () => this.initPromoStore()
        );
    }

    initPromoStore() {
        // if promo code global (query string, cookie)
        const savedCode = Cookies.get(PROMO_CODE_COOKIE_NAME);

        // Check if it is annual-upgrade page
        if (window.location.pathname.includes('/annual-upgrade')) {
            this.setIsAnnualUpgrade(true);
        }

        if (window.location.pathname.includes('expired/trial')) {
            this.setIsTrialExpired(true);
        }

        // Get session promo code
        const sessionStoredCode = getSessionStorage('sessionPromoCode');

        let code = ghPromoCode();

        // For Ruby plans, check only if url has Promo Code. Otherwise check other sources.
        if (!window.location.pathname.includes('/ruby')) {
            code = code || (this.isAnnualUpgrade && sessionStoredCode) || (this.isTrialExpired && cmsStore.promos.expiredTrialPromo) || savedCode || cmsStore.promos.annualPromo;
        }

        // If annual-upgrade page, set the coupon code in session storage
        if (this.isAnnualUpgrade) {
            setSessionStorage('sessionPromoCode', code);
            this.annualUpgradeCoupon = isString(code, this.annualUpgradeCoupon);
        }

        // if the promo cookie exist
        if (Cookies.get('promoModalViewed') === 'true') this.setPromoModalViewed(true);

        // if there is a promo stored as a cookie
        if (savedCode) this.setIsPromoStored(true);

        // if code exists
        if (code) this.validatePrimaryPromoCode(code);
    }

    @action setIsTrialExpired(bool) {
        this.isTrialExpired = isBoolean(bool, this.isTrialExpired);
    }

    @action setIsAnnualUpgrade(bool) {
        this.isAnnualUpgrade = isBoolean(bool, this.isAnnualUpgrade);
    }

    // set if promo was stored as a cookie
    @action setIsPromoStored(bool) {
        this.isPromoStored = isBoolean(bool, this.isPromoStored);
    }

    @action setPromo(obj) {
        this.promo = isObject(obj, this.promo);
    }

    @action setPromoSup(obj) {
        this.promoSup = isObject(obj, this.promoSup);
    }

    @action setTempPromo(obj) {
        this.promoTemp = isObject(obj, this.promoTemp);
    }

    @action setIsBannerOpen(bool) {
        if (bool) {
            Cookies.remove(PROMO_CODE_MODAL_CLOSED_COOKIE_NAME);
        } else {
            Cookies.set(
                PROMO_CODE_MODAL_CLOSED_COOKIE_NAME,
                true,
                { expires: EndOfDayDate(), domain }
            );
        }

        this.isBannerOpen = isBoolean(bool, this.isBannerOpen);
    }

    // set if promo modal was viewed here or in the marketing site
    @action setPromoModalViewed(bool) {
        this.PromoModalViewed = isBoolean(bool, this.PromoModalViewed);
    }

    @action setUsersCoupon(string) {
        this.userCoupon = isString(string, this.userCoupon);
    }

    @action validatePromoCode(code, callBack) {
        if (!code) return;
        axios.get(`/api/PromoCode/?promocodename=${code}`)
            .then((response) => {
                // data object
                const data = response.data;
                // if data and promo code is valid
                if (data && data.IsValid && callBack) callBack(data);
                // otherwise, do nothing
            }).catch((err) => {
                // throw error that promo code failed,
                // no need to tell user
                throw err;
            });
    }

    @action setSelectedPlanTermPromo() {
        when(
            () => cmsStore.hasLoaded,
            () => {
                if (this.promoTemp.PromoCodeName === cmsStore.promos.annualPromo ||
                    this.promoTemp.PromoCodeName === cmsStore.promos.monthlyPromo) {
                    const code = offerSuiteStore.isTermMonthly ? cmsStore.promos.monthlyPromo :
                        cmsStore.promos.annualPromo;
                    this.validatePromoCode(code, (data) => {
                        if (offerSuiteStore.isTermMonthly) {
                            this.setPromoSup(data);
                        } else {
                            this.setPromo(data);
                        }
                        this.setPlansDiscounts();
                        if (this.isAnnualUpgrade) {
                            setSessionStorage('sessionPromoCode', code);
                        } else {
                            Cookies.set(
                                PROMO_CODE_COOKIE_NAME,
                                code,
                                { expires: EndOfDayDate(), domain }
                            );
                        }
                        return true;
                    });
                }
            }
        );
    }

    // validate promo codes
    @action validatePrimaryPromoCode(code) {
        this.validatePromoCode(code, (data) => {
            // if coupon is annual only
            if (data.Name.indexOf(PROMO_CODE_ANNUAL_ONLY_STRING) !== -1) {
                this.setPromoSup({});
                // otherwise check price and cap it
            }
            // save coupon
            this.setPromo(data);
            this.setTempPromo(data);
            this.setPlansDiscounts();

            // View Promotion Banner event call
            gtm(viewPromotionEvent(data.PromoCodeName, data.Name));

            // set flag to open banner if it wasnt closed in the marketing site
            if (!Cookies.get(PROMO_CODE_MODAL_CLOSED_COOKIE_NAME)) this.setIsBannerOpen(true);
            // if the promo was not a previusly saved or is a new one and is not the annual insentive coupon
            if (!this.isPromoStored) {
                if (this.isAnnualUpgrade) {
                    setSessionStorage('sessionPromoCode', this.userCoupon || this.promoCodeName);
                } else {
                    Cookies.set(PROMO_CODE_COOKIE_NAME, this.userCoupon || this.promoCodeName, { expires: EndOfDayDate(), domain });
                }
            }

            return true;
        });
    }

    @computed get IsCouponAnnual() {
        return this.promo && this.promo.Name && this.promo.Name.startsWith(PROMO_CODE_ANNUAL_ONLY_STRING);
    }

    @computed get isCouponCapped() {
        return this.promoSup && this.promoSup.PromoCodeName && this.promoSup.PromoCodeName === PROMO_COUPON_MONTHLY_15;
    }

    @computed get isCouponValid() {
        const couponTypeArray = ['CappedDiscount', 'Discount', 'Credit'];
        return this.promoCodeName && includes(couponTypeArray, this.promoCodeType) && this.promoMaxAmount > 0;
    }

    @computed get activePromo() {
        return offerSuiteStore.isTermMonthly && isPlainObject(this.promoSup) ? this.promoSup : this.promo;
    }

    @computed get promoCodeName() {
        return isPlainObject(this.activePromo) && this.activePromo.PromoCodeName;
    }

    @computed get analyticsPromoCodeName() {
        // if active promo code
        if (isPlainObject(this.activePromo) && this.activePromo.PromoCodeName) {
            // if user was capped, send that coupon instead
            return this.userCoupon ? `${this.activePromo.PromoCodeName} - ${this.userCoupon}` : this.activePromo.PromoCodeName;
        }
        // this is so the values are consistent in the datalayer
        return undefined;
    }

    @computed get promoCodeId() {
        return isPlainObject(this.activePromo) && this.activePromo.Id;
    }

    @computed get promoCodeType() {
        return isPlainObject(this.activePromo) && this.activePromo.DiscountType;
    }

    @computed get promoCodeAmount() {
        const { selectedOfferSet, selectedPlanTerm } = offerSuiteStore;
        return isPlainObject(this.activePromo) && (this.discounts[selectedOfferSet[selectedPlanTerm].Id]?.discount || 0);
    }

    @computed get promoCodeMessage() {
        return isPlainObject(this.activePromo) && this.activePromo.Message;
    }

    @action setPlansDiscounts() {
        when(
            () => offerSuiteStore.fetchedOffers,
            () => {
                if (isPlainObject(this.activePromo)) {
                    // Tier Wise discounts for Tiered Discounts response
                    const tierWiseDiscounts = {};
                    // Fallback Tier Discounts object for Tiered Discounts response
                    let useLatestTierDiscounts = null;

                    // If Tiered Discounts
                    if (this.activePromo.DiscountTiers) {
                        // Set Tier Wise Discounts
                        this.activePromo.DiscountTiers.forEach((tier) => {
                            tierWiseDiscounts[`${tier.Tier}`] = tier;
                        });
                    }

                    // Set Discounts to the respective offer/plan IDs
                    offerSuiteStore.offerSuite.Offers.forEach((plan, idx) => {
                        // Tier Discounts object based off plan's `DiscountTier` value
                        const tierDiscounts = tierWiseDiscounts[plan.DiscountTier];
                        // If no Tier Discounts object available, then use fallback Tier Discounts Object(i.e. latest object)
                        const planDiscounts = tierDiscounts || useLatestTierDiscounts;
                        let discount;
                        let discountPercent;

                        // If Tiered Discounts
                        if (this.activePromo.DiscountTiers) {
                            if (planDiscounts) {
                                const isPercent = planDiscounts.IsPercentDiscount;
                                if (plan.Term === 'Monthly') {
                                    discountPercent = isPercent ? planDiscounts.MonthlyDiscountAmount * 100 : ((planDiscounts.MonthlyDiscountAmount / plan.monthlyPlanCost) * 100);
                                    discount = isPercent ? planDiscounts.MonthlyDiscountAmount * plan.monthlyPlanCost : planDiscounts.MonthlyDiscountAmount;
                                    if (discount) {
                                        this.discounts[plan.Id] = { discount, discountPercent: (discountPercent % 1) !== 0 ? discountPercent.toFixed(2) : discountPercent };
                                    }
                                } else {
                                    discountPercent = isPercent ? planDiscounts.AnnualDiscountAmount * 100 : ((planDiscounts.AnnualDiscountAmount / (plan.monthlyPlanCost * 12)) * 100);
                                    discount = isPercent ? planDiscounts.AnnualDiscountAmount * plan.monthlyPlanCost * 12 : planDiscounts.AnnualDiscountAmount;
                                    if (discount) {
                                        this.discounts[plan.Id] = { discount, discountPercent: (discountPercent % 1) !== 0 ? discountPercent.toFixed(2) : discountPercent };
                                    }
                                }
                                // latest/fallback Tier Discounts object with the current one
                                useLatestTierDiscounts = planDiscounts;
                                // Maximum promo amount
                                this.promoMaxAmount = Math.max(this.promoMaxAmount, discount);
                            }
                            // For `Percentage` discount
                        } else if (this.activePromo.DiscountApplyType === 'Percentage' && plan.Term !== 'Monthly') {
                            discountPercent = this.activePromo.PercentAmount * 100;
                            discount = this.activePromo.PercentAmount * plan.monthlyPlanCost * 12;
                            this.promoMaxAmount = Math.max(this.promoMaxAmount, discount);
                            this.discounts[plan.Id] = { discount, discountPercent: (discountPercent % 1) !== 0 ? discountPercent.toFixed(2) : discountPercent };
                        } else if (plan.Term !== 'Monthly') {
                            discountPercent = ((this.activePromo.Amount / (plan.monthlyPlanCost * 12)) * 100);
                            discount = this.activePromo.Amount;
                            this.promoMaxAmount = Math.max(this.promoMaxAmount, discount);
                            this.discounts[plan.Id] = { discount, discountPercent: (discountPercent % 1) !== 0 ? discountPercent.toFixed(2) : discountPercent };
                        }
                    });
                }
            }
        );
    }

    // Return if the promo is `Percentage` based
    @computed get isPercentPromo() {
        return isPlainObject(this.activePromo) && (this.activePromo.DiscountApplyType === 'TieredPercentage' || this.activePromo.DiscountApplyType === 'Percentage');
    }
}

const promoCodeStore = new PromoCodeClass();
export { promoCodeStore, PromoCodeClass };
